import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BookOpen, Calendar, Clock, MapPin, Users, Award, Eye, Star, GraduationCap, Search, Filter, Grid, List } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { format } from "date-fns";
import { formatCEU } from "@/lib/utils";

interface Course {
  id: string;
  title: string;
  description: string | null;
  hours: number;
  event_type: string;
  start_date: string | null;
  end_date: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  specific_location: string | null;
  max_participants: number | null;
  presenter: string | null;
  status: string;
  bosa_verified: boolean;
  category: string;
  subject_area: string;
  target_audience: string;
  event_banner_url: string | null;
  venue_details: string | null;
}

const PublicEvents = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [eventType, setEventType] = useState("all");
  const [sortOrder, setSortOrder] = useState("date");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);

  // Fetch public courses
  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["public-courses"],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isoToday = today.toISOString();
      const { data, error } = await supabase
        .from("courses")
        .select(`
          *,
          event_banner_url,
          venue_details
        `)
        .eq("status", "published")
        .gte("start_date", isoToday)
        .order("start_date", { ascending: true });

      if (error) throw error;
      return data as Course[];
    },
  });

  // Filter and sort courses
  const filteredCourses = courses
    .filter((course) => {
      // Only include courses with a start_date today or in the future
      if (!course.start_date || new Date(course.start_date) < new Date().setHours(0,0,0,0)) return false;
      const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        course.presenter?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = eventType === "all" || course.event_type === eventType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      if (sortOrder === "date") {
        return (a.start_date || "").localeCompare(b.start_date || "");
      } else if (sortOrder === "hours") {
        return b.hours - a.hours; // Sort by CEU hours descending
      }
      return a.title.localeCompare(b.title);
    });

  const stats = {
    totalCourses: courses.length,
    liveCourses: courses.filter(c => c.event_type === "live").length,
    selfPacedCourses: courses.filter(c => c.event_type === "self_paced").length,
    virtualCourses: courses.filter(c => c.event_type === "virtual").length,
  };

  const handleSignInClick = () => {
    navigate("/auth/login");
  };

  const handleCourseClick = (courseId: string) => {
    // Navigate to login with redirect to course details
    navigate(`/auth/login?redirect=/courses/${courseId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#004161] via-[#0f8f8c] to-[#004161] text-white shadow-lg mb-6">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-2 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="flex items-center gap-3">
            <BookOpen className="h-7 w-7 text-white drop-shadow-lg" />
            <div>
              <h1 className="text-xl md:text-2xl font-extrabold font-title tracking-tight leading-tight mb-0.5 drop-shadow">Professional Development Events</h1>
              <p className="text-sm font-serif text-white/80 max-w-2xl leading-tight">Discover, enroll, and track your professional learning opportunities. Filter by type, search, and view event details below.</p>
            </div>
          </div>
          <Button onClick={handleSignInClick} className="bg-white text-[#004161] font-bold shadow-lg hover:bg-blue-100 hover:text-[#0f8f8c] px-4 py-2 rounded-full text-base transition-all duration-200">
            Sign In to Enroll
          </Button>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">

        {/* Enhanced Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Filter & Search Events</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">View:</span>
              <div className="flex border rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className="rounded-none"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="rounded-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger>
                <SelectValue placeholder="Event Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="live">In-Person</SelectItem>
                <SelectItem value="self_paced">Self-Paced</SelectItem>
                <SelectItem value="virtual">Virtual</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger>
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date</SelectItem>
                <SelectItem value="title">Title</SelectItem>
                <SelectItem value="hours">CEU Hours</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-gray-600 flex items-center">
              <span>{filteredCourses.length} events found</span>
            </div>
          </div>
        </div>

        <Separator className="mb-6" />

        {/* Course Listings */}
        {isLoading ? (
          <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1"}`}>
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="animate-pulse overflow-hidden">
                <div className="h-48 bg-gray-200"></div>
                <CardHeader>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredCourses.length > 0 ? (
          <div className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1"}`}>
            {filteredCourses.map((course) => (
              <Card 
                key={course.id} 
                className={`group hover:shadow-xl transition-all duration-300 overflow-hidden ${
                  viewMode === "list" ? "flex flex-row" : "flex flex-col"
                }`}
              >
                {/* Course Banner */}
                <div className={`relative overflow-hidden ${
                  viewMode === "list" ? "w-80 flex-shrink-0" : "w-full h-48"
                }`}>
                  {course.event_banner_url ? (
                    <img
                      src={course.event_banner_url}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center">
                      <div className="text-center text-white">
                        <GraduationCap className="w-12 h-12 mx-auto mb-2 opacity-80" />
                        <p className="text-sm font-medium opacity-90">Professional Development</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Overlay Badges */}
                  <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                    <Badge 
                      variant={course.event_type === 'live' ? 'default' : 'secondary'}
                      className="shadow-lg backdrop-blur-sm"
                    >
                      {course.event_type === 'live' ? 'In-Person' : 
                       course.event_type === 'self_paced' ? 'Self-Paced' : 'Virtual'}
                    </Badge>
                    {course.bosa_verified && (
                      <Badge className="bg-yellow-500 text-yellow-900 shadow-lg backdrop-blur-sm">
                        <Award className="w-3 h-3 mr-1" />
                        BOSA Verified
                      </Badge>
                    )}
                  </div>

                  {/* Quick Action Overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          variant="secondary" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCourse(course);
                          }}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                      </DialogTrigger>
                    </Dialog>
                  </div>
                </div>

                {/* Course Content */}
                <div className="flex-1 flex flex-col">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <CardTitle className="text-lg line-clamp-2 group-hover:text-primary transition-colors">
                          {course.title}
                        </CardTitle>
                        {course.presenter && (
                          <CardDescription className="text-sm mt-1 flex items-center">
                            <Users className="w-3 h-3 mr-1" />
                            {course.presenter}
                          </CardDescription>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="flex items-center text-lg font-bold text-primary">
                          <Clock className="w-4 h-4 mr-1" />
                          {formatCEU(course.hours)} CEUs
                        </div>
                        {course.category && (
                          <Badge variant="outline" className="text-xs mt-1">
                            {course.category}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="flex-1 pt-0">
                    <div className="space-y-3">
                      {course.description && (
                        <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">
                          {course.description}
                        </p>
                      )}
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        {course.start_date && (
                          <div className="flex items-center text-gray-600">
                            <Calendar className="w-4 h-4 mr-2 text-primary" />
                            <span>
                              {format(new Date(course.start_date), "MMM d, yyyy")}
                            </span>
                          </div>
                        )}

                        {course.start_time && (
                          <div className="flex items-center text-gray-600">
                            <Clock className="w-4 h-4 mr-2 text-primary" />
                            <span>
                              {course.start_time}
                              {course.end_time && ` - ${course.end_time}`}
                            </span>
                          </div>
                        )}

                        {(course.venue_details || course.location) && (
                          <div className="flex items-center text-gray-600 sm:col-span-2">
                            <MapPin className="w-4 h-4 mr-2 text-primary" />
                            <span className="line-clamp-1">
                              {course.venue_details || course.location}
                            </span>
                          </div>
                        )}

                        {course.max_participants && (
                          <div className="flex items-center text-gray-600">
                            <Users className="w-4 h-4 mr-2 text-primary" />
                            <span>Max {course.max_participants} seats</span>
                          </div>
                        )}

                        <div className="flex items-center text-gray-600">
                          <Star className="w-4 h-4 mr-2 text-primary" />
                          <span>{course.target_audience === "all" ? "All Staff" : course.target_audience}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-4 border-t bg-gray-50/50">
                    <div className="flex gap-2 w-full">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="flex-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCourse(course);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </Button>
                        </DialogTrigger>
                      </Dialog>
                      <Button 
                        className="flex-1" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSignInClick();
                        }}
                      >
                        Sign In to Enroll
                      </Button>
                    </div>
                  </CardFooter>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <BookOpen className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
            <p className="text-gray-600">Try adjusting your search or filters.</p>
          </Card>
        )}

        {/* Call to Action */}
        <Card className="mt-12 bg-primary text-white">
          <CardContent className="text-center py-8">
            <h2 className="text-2xl font-bold mb-4">Ready to advance your career?</h2>
            <p className="mb-6 text-lg">Create an account to enroll in courses and track your professional development.</p>
            <Button size="lg" variant="secondary" onClick={() => navigate("/auth/register")}>
              Create Free Account
            </Button>
          </CardContent>
        </Card>

        {/* Course Details Modal */}
        {selectedCourse && (
          <Dialog open={!!selectedCourse} onOpenChange={() => setSelectedCourse(null)}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <DialogTitle className="text-2xl font-bold pr-8">
                      {selectedCourse.title}
                    </DialogTitle>
                    <DialogDescription className="mt-2 text-base">
                      {selectedCourse.presenter && `Presented by ${selectedCourse.presenter}`}
                    </DialogDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Badge 
                      variant={selectedCourse.event_type === 'live' ? 'default' : 'secondary'}
                      className="whitespace-nowrap"
                    >
                      {selectedCourse.event_type === 'live' ? 'In-Person' : 
                       selectedCourse.event_type === 'self_paced' ? 'Self-Paced' : 'Virtual'}
                    </Badge>
                    {selectedCourse.bosa_verified && (
                      <Badge className="bg-yellow-100 text-yellow-800 whitespace-nowrap">
                        <Award className="w-3 h-3 mr-1" />
                        BOSA Verified
                      </Badge>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6">
                {/* Course Banner */}
                {selectedCourse.event_banner_url && (
                  <div className="w-full h-64 rounded-lg overflow-hidden">
                    <img
                      src={selectedCourse.event_banner_url}
                      alt={selectedCourse.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Course Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center">
                      <BookOpen className="w-5 h-5 mr-2 text-primary" />
                      Course Information
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-3 text-primary" />
                        <span className="font-medium">{formatCEU(selectedCourse.hours)} CEU Hours</span>
                      </div>
                      
                      <div className="flex items-center">
                        <Star className="w-4 h-4 mr-3 text-primary" />
                        <span>Target: {selectedCourse.target_audience === "all" ? "All Staff" : selectedCourse.target_audience}</span>
                      </div>

                      {selectedCourse.category && (
                        <div className="flex items-center">
                          <GraduationCap className="w-4 h-4 mr-3 text-primary" />
                          <span>{selectedCourse.category}</span>
                        </div>
                      )}

                      {selectedCourse.subject_area && (
                        <div className="flex items-center">
                          <BookOpen className="w-4 h-4 mr-3 text-primary" />
                          <span>{selectedCourse.subject_area}</span>
                        </div>
                      )}

                      {selectedCourse.max_participants && (
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-3 text-primary" />
                          <span>Maximum {selectedCourse.max_participants} participants</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-primary" />
                      Schedule & Location
                    </h3>
                    
                    <div className="space-y-3">
                      {selectedCourse.start_date && (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-3 text-primary" />
                          <div>
                            <div className="font-medium">
                              {format(new Date(selectedCourse.start_date), "EEEE, MMMM d, yyyy")}
                            </div>
                            {selectedCourse.end_date && selectedCourse.start_date !== selectedCourse.end_date && (
                              <div className="text-sm text-gray-600">
                                to {format(new Date(selectedCourse.end_date), "EEEE, MMMM d, yyyy")}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {selectedCourse.start_time && (
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-3 text-primary" />
                          <span>
                            {selectedCourse.start_time}
                            {selectedCourse.end_time && ` - ${selectedCourse.end_time}`}
                          </span>
                        </div>
                      )}

                      {(selectedCourse.venue_details || selectedCourse.location) && (
                        <div className="flex items-start">
                          <MapPin className="w-4 h-4 mr-3 text-primary mt-0.5" />
                          <div>
                            <div className="font-medium">
                              {selectedCourse.venue_details || selectedCourse.location}
                            </div>
                            {selectedCourse.venue_details && selectedCourse.location && (
                              <div className="text-sm text-gray-600">
                                {selectedCourse.location}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Course Description */}
                {selectedCourse.description && (
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold">Course Description</h3>
                    <div className="prose prose-sm max-w-none">
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {selectedCourse.description}
                      </p>
                    </div>
                  </div>
                )}

                {/* BOSA Information */}
                {selectedCourse.bosa_verified && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-yellow-800">BOSA Verified Course</h4>
                        <p className="text-sm text-yellow-700 mt-1">
                          This course has been verified for BOSA certificate eligibility. Upon completion, 
                          you'll be able to request conversion of your CEU certificate to a BOSA certificate 
                          for license renewal purposes.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="flex flex-col sm:flex-row gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setSelectedCourse(null)}
                  className="sm:order-1"
                >
                  Close
                </Button>
                <Button 
                  onClick={handleSignInClick}
                  className="sm:order-2"
                >
                  Sign In to Enroll
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
};

export default PublicEvents;